// Dependencies
import DatePicker from "react-datepicker";
import React, { useEffect, useState } from "react";
import { format, startOfDay, subDays } from "date-fns";
import { LineChart, Line, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer } from "recharts";

// Components
import Button from "Components/Button";

// Hooks
import useRequest from "Hooks/useRequest";

import css from "./RequestsReport.styl";
import "react-datepicker/dist/react-datepicker.css";

const commafy = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const Graph = ({ date, globalAxis, domainMean, domainRequestCount, onComputeDomainMax }) => {
	const dateString = date.toISOString().slice(0, 10);
	const timeZoneOffsetMilliseconds = date.getTimezoneOffset() * 60 * 1000;
	const { data, isRequesting } = useRequest("Get Request Report", {
		filters: { date: dateString, timeZoneOffsetMilliseconds },
	});

	let requestData = [];
	let hasRequestData = false;

	if (!isRequesting && data && data.requests) {
		hasRequestData = true;
		requestData = data.requests;
	}

	const maxMeanResponseTime = Math.max(
		...requestData.map((req) => {
			if (req && req.meanResponseTime) {
				return req.meanResponseTime;
			}

			return 0;
		})
	);

	const maxRequestCount = Math.max(
		...requestData.map((req) => {
			if (req && req.requestCount) {
				return req.requestCount;
			}

			return 0;
		})
	);

	const weightedMeanResponseTime = requestData.reduce((acc, cur) => {
		if (!cur || !cur.meanResponseTime) {
			return acc;
		}

		const value = acc + cur.meanResponseTime * cur.requestCount;
		return value;
	}, 0);

	const totalRequests = requestData.reduce((acc, cur) => {
		if (!cur || !cur.requestCount) {
			return acc;
		}

		const value = acc + cur.requestCount;
		return value;
	}, 0);

	const meanResponseTime = hasRequestData ? weightedMeanResponseTime / totalRequests : 0;

	useEffect(() => {
		onComputeDomainMax("meanResponse", maxMeanResponseTime);
	}, [maxMeanResponseTime]);

	useEffect(() => {
		onComputeDomainMax("requestCount", maxRequestCount);
	}, [maxRequestCount]);

	return (
		<div className={css.parent}>
			<div className={css.graphWrapper}>
				<ResponsiveContainer width="100%" height={140}>
					<LineChart syncId="anyId" data={requestData}>
						<XAxis dataKey="unixTime" hide={true} />
						<YAxis yAxisId="mrt" domain={[0, globalAxis ? domainMean : maxMeanResponseTime]} hide={true} />
						<YAxis
							yAxisId="rc"
							domain={[0, globalAxis ? domainRequestCount : maxRequestCount]}
							hide={true}
						/>
						<Tooltip labelFormatter={(t) => format(new Date(t * 1000), "hh:mm a")} />
						<ReferenceLine
							yAxisId="mrt"
							strokeWidth={2}
							strokeDasharray="3 3"
							y={meanResponseTime}
							stroke="rgba(221,78,68, 0.5)"
							label={{
								position: "insideBottomRight",
								value: "Mean Response Time",
								fill: "red",
								fontSize: 14,
							}}
						/>
						<Line
							yAxisId="rc"
							strokeWidth={2}
							type="monotone"
							dataKey="requestCount"
							stroke="#e09f34"
							dot={false}
						/>
						<Line
							yAxisId="mrt"
							strokeWidth={2}
							type="monotone"
							dataKey="meanResponseTime"
							stroke="#24b2e0"
							dot={false}
						/>
						{/* <Line strokeWidth={2} type="monotone" dataKey="maxResponseTime" stroke="#3398d9" dot={false} /> */}
					</LineChart>
				</ResponsiveContainer>
			</div>
			<div className={css.stats}>
				<div>
					<div className={css.stat}>
						<div className={css.value}>{format(date, "E, MMM d")}</div>
					</div>

					<div className={css.stat}>
						<div className={css.label}>Total Requests</div>
						<div className={css.value}>{commafy(totalRequests)}</div>
					</div>

					<div className={css.stat}>
						<div className={css.label}>Mean Response Time</div>
						<div className={css.value}>{meanResponseTime.toFixed(2)} ms</div>
					</div>

					<Button
						href={`/slow-queries/${dateString}/${timeZoneOffsetMilliseconds}`}
						className={css.viewSlowQueries}
					>
						View Slow Queries
					</Button>
				</div>
			</div>
		</div>
	);
};

const RequestsReport = () => {
	const graphCount = 28;
	const [domainMean, setDomainMean] = useState(100);
	const [globalAxis, setGlobalAxis] = useState(true);
	const [date, setDate] = useState(startOfDay(new Date()));
	const [domainRequestCount, setDomainRequestCount] = useState(100);

	const handleDateChange = (value) => {
		setDate(startOfDay(value));
	};

	const handleGlobalAxisChange = (event) => {
		setGlobalAxis(event.target.checked);
	};

	const handleComputeDomainMax = (type, value) => {
		if (type === "meanResponse") {
			if (value > domainMean) {
				setDomainMean(value);
			}
		} else if (type === "requestCount") {
			if (value > domainRequestCount) {
				setDomainRequestCount(value);
			}
		}
	};

	const isTodayOrEarlier = (value) => {
		const today = new Date();
		const todayStart = startOfDay(today);
		const dateStart = startOfDay(value);

		return dateStart <= todayStart;
	};

	return (
		<div className={css.main}>
			<div className={css.controls}>
				<div>
					<div className={css.control}>
						<div className={css.label}>Date</div>
						<div className={css.input}>
							<DatePicker
								filterDate={isTodayOrEarlier}
								dateFormat="MMMM d"
								selected={date}
								onChange={handleDateChange}
							/>
						</div>
					</div>

					<div className={css.control}>
						<div className={css.label}>Global Axis</div>
						<div className={css.input}>
							<input type="checkbox" checked={globalAxis} onChange={handleGlobalAxisChange} />
						</div>
					</div>
				</div>
			</div>

			<div className={css.graphs}>
				{Array.from({ length: graphCount }).map((_, index) => {
					const graphDate = subDays(date, index);
					const key = graphDate.toISOString();

					return (
						<div key={key} className={css.chart}>
							<Graph
								date={graphDate}
								globalAxis={globalAxis}
								domainMean={domainMean}
								domainRequestCount={domainRequestCount}
								onComputeDomainMax={handleComputeDomainMax}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default RequestsReport;
